import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';
import { activityInitialState, activityReducers } from '../_shared/activityState';

const initialState = {
	data: {},
	timeStamp: null,
	loading: false,
	loadingAddress: false,
	loadingCreate: false,
	// The siteID the last create returned — the Add site modal's step 2 (floor plan) cannot
	// address the broker without it, since a site's media is keyed by the row that now exists.
	// Mirrors floors' `createdFloorID`; null again on the next create and on failure, so a failed
	// create can never advance a wizard.
	createdSiteID: null,
	loadingUpdate: false,
	loadingDelete: false,
	mapLoading: false,
	sitesLoadingComplete: false,
	error: null,
	activity: { ...activityInitialState }
};

const sitesSlice = createSlice({
	name: 'sites',
	initialState,
	reducers: {
		loadSites(state) {
			state.loading = true;
			state.error = null;
		},
		loadSitesSuccess(state, action) {
			state.timeStamp = Date.now();
			state.loading = false;
			const orgID = action.payload.orgID;
			const oldSitesData = state.data[orgID] || [];

			// Merge sites data using 'siteID' as the unique identifier
			state.data[orgID] = mergeReduxStateData(oldSitesData, action.payload.data, 'siteID');

			state.sitesLoadingComplete = true;
		},
		loadSiteAddress(state) {
			state.loadingAddress = true;
			state.error = null;
		},
		loadSiteAddressSuccess(state, action) {
			state.data[action.payload.orgID] = state.data[action.payload.orgID].map((site) =>
				site.siteID === action.payload.siteID
					? { ...site, address: { data: action.payload.data, timeStamp: Date.now() } }
					: site
			);
			state.loadingAddress = false;
		},
		loadSiteAddressError(state, action) {
			state.error = action.payload;
			state.loadingAddress = false;
		},
		loadSitesError(state, action) {
			state.error = action.payload;
			state.loading = false;
		},
		createSite(state) {
			state.loadingCreate = true;
			state.error = null;
			state.createdSiteID = null;
		},
		createSiteSuccess(state, action) {
			state.loadingCreate = false;
			state.createdSiteID = action.payload?.siteID ?? null;
		},
		createSiteError(state, action) {
			state.error = action.payload;
			state.loadingCreate = false;
			state.createdSiteID = null;
		},
		updateSite(state) {
			state.loadingUpdate = true;
			state.error = null;
		},
		updateSiteSuccess(state) {
			state.loadingUpdate = false;
		},
		updateSiteError(state, action) {
			state.error = action.payload;
			state.loadingUpdate = false;
		},
		// Delete site — the modal closes optimistically on dispatch; this flag only
		// drives the topbar in-flight indicator, so no success flag is needed.
		deleteSite(state) {
			state.loadingDelete = true;
			state.error = null;
		},
		deleteSiteSuccess(state) {
			state.loadingDelete = false;
		},
		deleteSiteError(state, action) {
			state.error = action.payload;
			state.loadingDelete = false;
		},
		loadSiteMap: (state) => {
			state.mapLoading = true;
			state.error = null;
		},
		loadSiteMapSuccess: (state, action) => {
			state.mapLoading = false;
			state.data[action.payload.orgID] = state.data[action.payload.orgID].map((site) =>
				site.siteID === action.payload.siteID
					? { ...site, map: { data: action.payload.data, timeStamp: Date.now() } }
					: site
			);
		},
		loadSiteMapError: (state, action) => {
			state.mapLoading = false;
			state.error = action.payload;
		},
		...activityReducers
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// Org switch: reset loading flags so useScopeUrlSync waits for fresh site data
		builder.addCase('auth/switchProfileSuccess', (state) => {
			state.sitesLoadingComplete = false;
			state.loading = false;
		});
	}
});

export const sitesActions = sitesSlice.actions;
export default sitesSlice.reducer;
